import { useIsDarkMode } from '@flowus/common';
import { Button } from '@flowus/common/components';
import { message } from '@flowus/common/components/message';
import { googleFirebaseEvent } from '@flowus/common/google-firebase';
import { useOpenModal } from '@flowus/common/next-modal';
import type { PreOrderDTO } from '@next-space/fe-api-idl';
import { getIsApp } from '@flowus/common/utils/get-is-app';
import {
  Elements,
  LinkAuthenticationElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { Layout, Stripe } from '@stripe/stripe-js';
import type { FC, FormEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { colors } from 'src/colors';
import { createModel, useModel } from 'src/common/create-model';
import { VITE_STRIPE_PUBLIC_KEY } from 'src/env';
import { Modals } from 'src/modals';
import { useCloseAllModal } from 'src/redux/managers/ui';
import { useStripeSuccess } from './select-upgrade-plan/hook/use-upgrade-support';
import { useCheckOrder } from 'src/views/upgrade/h5/use-check-order';
import { PageScene, usePageScene } from 'src/views/main/scene-context';

// #region types
interface StripeFormProps {
  submit: () => void;
  className?: string;
}
interface StripeFormContextProps {
  clientSecret: string;
}
// #endregion

// #region checkout
const useStripeCheckOut = (props: { submit: () => void }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const openModal = useOpenModal();
  const closeAllModal = useCloseAllModal();

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!stripe || !elements || isLoading) {
      return;
    }

    openModal.loading({ modalId: Modals.STRIPE_CHECKOUT, autoClose: false });
    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: location.origin,
      },
      redirect: 'if_required',
    });

    closeAllModal();
    if (!error) {
      props.submit();
    } else {
      message.error(error.message ?? 'An unexpected error occurred.');
    }

    setIsLoading(false);
  };

  return { onSubmit, email, isLoading, setEmail };
};

const StripeCheckOutContext = createModel(useStripeCheckOut);
export const useStripeCheckOutContext = () => useModel(StripeCheckOutContext);
export const StripeCheckoutForm: FC = () => {
  const { setEmail } = useStripeCheckOutContext();
  return (
    <>
      <LinkAuthenticationElement
        id="link-authentication-element"
        onChange={(e) => setEmail(e.value.email)}
      />
      <PaymentElement id="payment-element" options={{ layout: 'tabs' as Layout }} />
    </>
  );
};
// #endregion

// #region form
const useStripeForm = (props: StripeFormContextProps) => {
  const { clientSecret } = props;
  const isDarkModel = useIsDarkMode();

  const appearance = {
    variables: isDarkModel
      ? {
          colorPrimary: colors.active_color,
          colorBackground: colors.dark.white2,
          colorText: colors.dark.white,
        }
      : undefined,
  };

  const options = {
    clientSecret,
    appearance,
    locale: 'en',
  };

  return { options, clientSecret };
};

const StripeFormContext = createModel(useStripeForm);
const useStripeFormContext = () => useModel(StripeFormContext);

export const StripeForm: FC<StripeFormProps & StripeFormContextProps> = (props) => {
  const { clientSecret, submit, children, className } = props;
  return (
    <StripeFormContext clientSecret={clientSecret}>
      <StripeFormContent className={className} submit={submit}>
        {children}
      </StripeFormContent>
    </StripeFormContext>
  );
};

const StripeFormContent: FC<StripeFormProps> = (props) => {
  const { options } = useStripeFormContext();
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    void (async () => {
      const { loadStripe } = await import('@stripe/stripe-js');
      setStripePromise(loadStripe(VITE_STRIPE_PUBLIC_KEY));
    })();
  }, []);

  return (
    <>
      {options.clientSecret && (
        <Elements key={options.clientSecret} options={options as any} stripe={stripePromise}>
          <StripeCheckOutContext submit={props.submit}>
            <StripeFormSubmit className="p-5 space-y-5">
              <StripeCheckoutForm />
              <Button colorType="active">Pay</Button>
            </StripeFormSubmit>
          </StripeCheckOutContext>
        </Elements>
      )}
    </>
  );
};

const StripeFormSubmit: FC<{ className?: string }> = (props) => {
  const { className, children } = props;
  const { onSubmit } = useStripeCheckOutContext();

  return (
    <form
      id="payment-form"
      // className={cx('animate__animated animate__fadeIn', className)}
      className={className}
      onSubmit={onSubmit}
    >
      {children}
    </form>
  );
};
// #endregion

export const useOpenStripe = () => {
  const openModal = useOpenModal();
  const stripeSuccess = useStripeSuccess();
  const isInTemplateCenter = usePageScene() === PageScene.TEMPLATE;
  return useCallback(
    (opt: { clientSecret?: string }) => {
      if (!opt.clientSecret) {
        return;
      }

      openModal.modal({
        content: (_) => (
          <StripeForm
            submit={() => {
              _.onCloseModal();
              if (isInTemplateCenter) return;
              void stripeSuccess();
              if (getIsApp()) {
                setTimeout(() => {
                  window.close();
                }, 5000);
              }
            }}
            clientSecret={`${opt.clientSecret}`}
          />
        ),
      });
    },
    [isInTemplateCenter, openModal, stripeSuccess]
  );
};

export const useOpenIframePay = () => {
  const openModal = useOpenModal();
  const isInTemplateCenter = usePageScene() === PageScene.TEMPLATE;
  return useCallback(
    (params: { url?: string }) => {
      if (!params.url) {
        return;
      }
      window.open(params.url);
      setTimeout(() => {
        if (!document.hidden) {
          // 如果window.open被阻止了，就走下面的逻辑
          const a = document.createElement('a');
          a.href = params.url ?? '';
          a.target = '_blank';
          a.click();
        }
        if (isInTemplateCenter) return;
        openModal.warning({
          title: '已完成支付？',
          colorType: 'active',
          cancelText: '取消',
          confirmText: '已支付',
          confirm() {
            location.reload();
          },
        });
        if (!document.hidden) {
          //
        }
      }, 500);
    },
    [isInTemplateCenter, openModal]
  );
};

export const useOpenBuildInPay = () => {
  const openStripe = useOpenStripe();
  const openIframePay = useOpenIframePay();
  return useCallback(
    (order: PreOrderDTO) => {
      if (order.redirectUrl) {
        openIframePay({ url: order.redirectUrl });
      } else {
        openStripe({ clientSecret: order.clientSecret });
      }
    },
    [openIframePay, openStripe]
  );
};
